import { useFulfillmentDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/store/fulfillmentDashboard.store';
import { KpiStatCardProps } from 'pages/ReportsPage/components/AnalyticsDashboards/KpiStatCard';
import { formatDashboardNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/formatDashboardNumber';
import { isSelectionCleared } from 'pages/ReportsPage/store/analyticsSelection.utils';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { buildClearedCard } from 'pages/ReportsPage/components/AnalyticsDashboards/kpiCardBuilders';

function formatNumber(value: number): string {
  return value.toLocaleString();
}

export function useFulfillmentKpiCards(): KpiStatCardProps[] {
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const totalAssignedShifts = useFulfillmentDashboardStore((s) => s.totalAssignedShifts);
  const totalAssignedShiftsPeriodDays = useFulfillmentDashboardStore(
    (s) => s.totalAssignedShiftsPeriodDays,
  );
  const totalAssignedShiftsChangePrct = useFulfillmentDashboardStore(
    (s) => s.totalAssignedShiftsChangePrct,
  );
  const totalAssignedShiftsError = useFulfillmentDashboardStore((s) => s.totalAssignedShiftsError);
  const totalCalloutShifts = useFulfillmentDashboardStore((s) => s.totalCalloutShifts);
  const totalCalloutShiftsPeriodDays = useFulfillmentDashboardStore(
    (s) => s.totalCalloutShiftsPeriodDays,
  );
  const totalCalloutShiftsChangePrct = useFulfillmentDashboardStore(
    (s) => s.totalCalloutShiftsChangePrct,
  );
  const totalCalloutShiftsError = useFulfillmentDashboardStore((s) => s.totalCalloutShiftsError);
  const fulfillmentRate = useFulfillmentDashboardStore((s) => s.fulfillmentRate);
  const fulfillmentRatePeriodDays = useFulfillmentDashboardStore(
    (s) => s.fulfillmentRatePeriodDays,
  );
  const fulfillmentRateChangePrct = useFulfillmentDashboardStore(
    (s) => s.fulfillmentRateChangePrct,
  );
  const fulfillmentRateError = useFulfillmentDashboardStore((s) => s.fulfillmentRateError);
  const cancelledShifts = useFulfillmentDashboardStore((s) => s.cancelledShifts);
  const cancelledShiftsPeriodDays = useFulfillmentDashboardStore(
    (s) => s.cancelledShiftsPeriodDays,
  );
  const cancelledShiftsChangePrct = useFulfillmentDashboardStore(
    (s) => s.cancelledShiftsChangePrct,
  );
  const cancelledShiftsError = useFulfillmentDashboardStore((s) => s.cancelledShiftsError);

  const totalAssignedComparisonText =
    totalAssignedShiftsPeriodDays != null
      ? `vs. prior ${totalAssignedShiftsPeriodDays} days`
      : undefined;
  const cancelledComparisonText =
    cancelledShiftsPeriodDays != null ? `vs. prior ${cancelledShiftsPeriodDays} days` : undefined;
  const totalCalloutComparisonText =
    totalCalloutShiftsPeriodDays != null
      ? `vs. prior ${totalCalloutShiftsPeriodDays} days`
      : undefined;
  const fulfillmentRateComparisonText =
    fulfillmentRatePeriodDays != null ? `vs. prior ${fulfillmentRatePeriodDays} days` : undefined;

  const cards: KpiStatCardProps[] = [
    totalAssignedShiftsError
      ? {
          label: 'Total Assigned Shifts',
          value: '--',
          subtitle: 'Failed to load',
          subtitleVariant: 'error' as const,
        }
      : {
          label: 'Total Assigned Shifts',
          value: formatNumber(totalAssignedShifts),
          ...(totalAssignedShiftsChangePrct != null && {
            changePercentage: totalAssignedShiftsChangePrct,
            comparisonText: totalAssignedComparisonText,
          }),
        },
    totalCalloutShiftsError
      ? {
          label: 'Total Call Out Shifts',
          value: '--',
          subtitle: 'Failed to load',
          subtitleVariant: 'error' as const,
        }
      : {
          label: 'Total Call Out Shifts',
          value: formatNumber(totalCalloutShifts),
          ...(totalCalloutShiftsChangePrct != null && {
            changePercentage: totalCalloutShiftsChangePrct,
            comparisonText: totalCalloutComparisonText,
          }),
        },
    fulfillmentRateError
      ? {
          label: 'Fulfillment Rate',
          value: '--',
          subtitle: 'Failed to load',
          subtitleVariant: 'error' as const,
        }
      : {
          label: 'Fulfillment Rate',
          value: `${formatDashboardNumber(fulfillmentRate)}%`,
          ...(fulfillmentRateChangePrct != null && {
            changePercentage: fulfillmentRateChangePrct,
            comparisonText: fulfillmentRateComparisonText,
          }),
        },
    cancelledShiftsError
      ? {
          label: 'Cancelled Shifts',
          value: '--',
          subtitle: 'Failed to load',
          subtitleVariant: 'error' as const,
        }
      : {
          label: 'Cancelled Shifts',
          value: formatNumber(cancelledShifts),
          ...(cancelledShiftsChangePrct != null && {
            changePercentage: cancelledShiftsChangePrct,
            comparisonText: cancelledComparisonText,
          }),
        },
  ];

  return isSelectionCleared(locationIds, positions)
    ? cards.map((card) => buildClearedCard(card.label))
    : cards;
}
